import React, { useEffect, useState } from "react";
import {
  clearAuthenticatedUser,
  getAuthenticatedUser,
  subscribeToLogout,
} from "./helpers/auth-session";
import { provisionActiveFirmCatalogues } from "./helpers/backend_helper";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "./components/Common/AppOverlay";
import { allowLogoutNavigation } from "./helpers/unsaved-changes";
import "./App.css";
import "./components/Help/GuidedTour.scss";

import PropTypes from "prop-types";
import { Outlet, Routes, Route } from "react-router-dom";
import { connect } from "react-redux";

// Import Routes all
import { userRoutes, authRoutes } from "./routes/allRoutes";

// Import all middleware
import Authmiddleware from "./routes/middleware/Authmiddleware";

// layouts Format
import AppLayout from "./components/AppLayout/";
import NonAuthLayout from "./components/NonAuthLayout";
import HelpLayout from "./pages/Help/HelpLayout";

// Import scss
import "./assets/scss/theme.scss";
import "./assets/scss/preloader.scss";
import "./assets/scss/_form-controls.scss";

import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";

import { GlobalLoading, showLoading } from "react-global-loading";
import { Slide, ToastContainer, toast } from "react-toastify";
import { NotificationProvider } from "./contexts/NotificationContext";
import SessionTimeoutManager from "./components/Authentication/SessionTimeoutManager";

const App = ({ instance }) => {
  const [cataloguePromptOpen, setCataloguePromptOpen] = useState(false);
  const [cataloguePublishing, setCataloguePublishing] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToLogout((reason) => {
      clearAuthenticatedUser();
      allowLogoutNavigation();
      if (window.location.pathname !== "/signin")
        window.location.replace(
          reason === "expired"
            ? "/signin?sessionExpired=true"
            : "/signin?signedOut=true"
        );
    });

    const onPageShow = (event) => {
      if (event.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      unsubscribe();
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);
  useEffect(() => {
    const prompt = () => {
      if (Number(getAuthenticatedUser()?.firmId) === 1)
        setCataloguePromptOpen(true);
    };
    window.addEventListener("piers:system-catalogue-changed", prompt);
    return () =>
      window.removeEventListener("piers:system-catalogue-changed", prompt);
  }, []);

  const applyCatalogue = async () => {
    setCataloguePublishing(true);
    try {
      const response = await provisionActiveFirmCatalogues();
      if (!response?.status)
        throw new Error(response?.message || "Unable to apply the catalogue.");
      toast.success(
        `Applied ${
          response.data?.copied || 0
        } new active catalogue item(s) to ${response.data?.firms || 0} firm(s).`
      );
      setCataloguePromptOpen(false);
    } catch (error) {
      toast.error(error.message || "Unable to apply the active catalogue.");
    } finally {
      setCataloguePublishing(false);
    }
  };
  return (
    <React.Fragment>
      <GlobalLoading
        zIndex="100000"
        loadingThickness="10"
        loadingColor="#273F79"
        loadingBackgroundColor="white"
        loadingType="spin"
      ></GlobalLoading>
      <ToastContainer autoClose={2000} />
      <SessionTimeoutManager />

      <NotificationProvider>
        <Routes>
          {authRoutes.map((route, idx) => (
            <Route
              key={idx}
              path={route.path}
              element={
                <Authmiddleware layout={NonAuthLayout} isAuthProtected={false}>
                  <route.element />
                </Authmiddleware>
              }
            />
          ))}

          {userRoutes.filter((route) => route.layout === "help").map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <Authmiddleware layout={HelpLayout} isAuthProtected={true}>
                  <route.element />
                </Authmiddleware>
              }
            />
          ))}

          <Route
            element={
              <Authmiddleware layout={AppLayout} isAuthProtected={true}>
                <Outlet />
              </Authmiddleware>
            }
          >
            {userRoutes.filter((route) => route.layout !== "help").map((route, idx) => (
              <Route key={idx} path={route.path} element={<route.element />} />
            ))}
          </Route>
        </Routes>
      </NotificationProvider>

      <Modal
        isOpen={cataloguePromptOpen}
        toggle={() => !cataloguePublishing && setCataloguePromptOpen(false)}
        centered
      >
        <ModalHeader
          toggle={() => !cataloguePublishing && setCataloguePromptOpen(false)}
        >
          Apply this change to all active firms?
        </ModalHeader>
        <ModalBody>
          PIERS will add new active system catalogue records and update
          system-provided copies that firms have not customized. Firm-created
          and customized records remain unchanged.
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={cataloguePublishing}
            onClick={() => setCataloguePromptOpen(false)}
          >
            No, keep system only
          </Button>
          <Button
            color="primary"
            disabled={cataloguePublishing}
            onClick={applyCatalogue}
          >
            {cataloguePublishing ? "Applying…" : "Yes, apply to active firms"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

App.propTypes = {
  layout: PropTypes.any,
};

const mapStateToProps = (state) => {
  return {
    layout: state.Layout,
  };
};

export default connect(mapStateToProps, null)(App);
